import React, { createContext } from "react";
// UI Framework
// Hooks
import { useAppSelector } from "src/App/hooks";
// Common Components
import { CheckBox, FileDownloader, Button, Modal } from "src/Components";
import TableTemplate, {
  IExtraTypeUIProps,
  IFilterTableCols,
} from "src/Tables/TableTemplate/tableTemplate";
import SelectableButtons from "src/Components/Dropdown/SelectableButtons";
import { CommonTableUI } from "src/Tables/TableTemplate/common";
// Local Components
import OrderBook from "./OrderBook";
import SearchParamUI from "./SearchParamUI";
import ExpectedReturnModal from "./ExpectedReturnModal";
import FilterSettingModal from "./FilterSettingModal";
import ChartSearchParamUI from "../ChartSearchParamUI";
// Requests
import { getOffsetExcel, getOffsetTable } from "src/Requests/coveredCall";
// Utilities
import * as R from "ramda";
// Types
import { ExtraType } from "src/Interfaces/TableInterfaces";
import {
  IEnterPairPositionSearchParams,
  ISelectedTableInfo,
  ICoveredCallOffsetTableReport,
} from "src/Interfaces/coveredCall";
import { Additional } from "./Additional";

interface IVisibleHeaders {
  all: boolean;
  mediator: boolean;
}

enum ModalMode {
  Closed,
  ExpectedReturn,
  OpenningSettings,
  FilterSetting,
}

export const CoveredCallOffsetContext = createContext({ riskProfileId: "" });

const Offset: React.FC = () => {
  // refs
  const prevRowRef = React.useRef<number | null>(null);
  // store
  const riskProfiles = useAppSelector(
    (store) => store.coveredCall.riskProfiles,
  );
  // local store
  const [modalMode, setModalMode] = React.useState(ModalMode.Closed);
  const [searchParam, setSearchParam] =
    React.useState<IEnterPairPositionSearchParams>({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
    } as IEnterPairPositionSearchParams);
  const [serverSearchParams, setServerSearchParams] =
    React.useState<IEnterPairPositionSearchParams>({
      riskProfileId: riskProfiles[0]?.id?.toString() ?? 0,
    } as IEnterPairPositionSearchParams);
  const [visibleHeaders, setVisibleHeaders] = React.useState(
    {} as IVisibleHeaders,
  );
  const [selectedTableInfo, setSelectedTableInfo] =
    React.useState<ISelectedTableInfo>({} as ISelectedTableInfo);
  const [refreshFlag, setRefreshFlag] = React.useState(false);

  const changeHandler = (name: string, value: any) => {
    setVisibleHeaders({ ...visibleHeaders, [name]: value } as IVisibleHeaders);
  };

  const handleExcelDownload = () => getOffsetExcel(searchParam);

  const searchParamChangeHandler = (name: string, value: any) => {
    setSearchParam({
      ...searchParam,
      [name]: value,
    });
  };

  const handleSearch = () => {
    const cleanObject = R.pickBy(
      (v) => !(v === null || v === "" || (Array.isArray(v) && v.length === 0)),
      searchParam,
    );
    setServerSearchParams(cleanObject as IEnterPairPositionSearchParams);
  };

  const getFilterCols = (): IFilterTableCols => {
    const result: IFilterTableCols = {
      show: true,
      tableName: "coveredCallPairPositionTable",
      allowedPositions: [],
    };
    if (visibleHeaders.all) result.allowedPositions.push("DefaultHidden");
    if (visibleHeaders.mediator) result.allowedPositions.push("Mediator");
    return result;
  };

  const riskProfileChangeHandler = (name: string, value: any) => {
    searchParamChangeHandler(name, value);
    setServerSearchParams({ ...serverSearchParams, [name]: value });
  };

  const UIModal = React.useMemo(() => {
    switch (modalMode) {
      case ModalMode.ExpectedReturn:
        return (
          <ExpectedReturnModal onClose={() => setModalMode(ModalMode.Closed)} />
        );
      case ModalMode.FilterSetting:
        return (
          <FilterSettingModal
            onClose={() => setModalMode(ModalMode.Closed)}
            onApplied={() => setRefreshFlag((prev) => !prev)}
          />
        );
    }
    return null;
  }, [modalMode]);

  const getExtraTypeUI = React.useCallback(
    ({
      headerName,
      extraType,
      row,
      cellType,
    }: IExtraTypeUIProps<ICoveredCallOffsetTableReport>) => {
      switch (extraType) {
        case ExtraType.WithChart:
          return <ChartSearchParamUI {...{ row, headerName }} />;

        default:
          return <></>;
      }
    },
    [],
  );

  return (
    <>
      {modalMode ? (
        <Modal title={modalMode === ModalMode.ExpectedReturn ? "بازده انتظاری" : "تنظیمات فیلتر"} UI={UIModal} onClose={() => setModalMode(ModalMode.Closed)} />
      ) : (
        <></>
      )}
      <div className="flex flex-col justify-start items-start gap-2 relative pt-5 mx-auto p-8 w-full">
        <div className="flex justify-start items-center w-full">
          <div className="flex flex-row justify-start items-center gap-5">
            <div className="bg-sky-200 rounded py-1 px-2">
              <CheckBox
                checked={visibleHeaders.all}
                label="جزئیات"
                name="all"
                onChange={changeHandler}
              />
            </div>
            <div className="bg-red-200 rounded py-1 px-2">
              <CheckBox
                checked={visibleHeaders.mediator}
                label="محاسبات"
                name="mediator"
                onChange={changeHandler}
              />
            </div>
            <FileDownloader getFile={handleExcelDownload} />
            <Button
              onClick={() => setModalMode(ModalMode.ExpectedReturn)}
              text="بازده انتظاری"
              variant="outlined"
            />
            <Button
              onClick={() => setModalMode(ModalMode.FilterSetting)}
              text="تنظیمات فیلتر"
              variant="outlined"
            />
          </div>
        </div>
        <OrderBook selectedTableInfo={selectedTableInfo} />
        <div className="flex flex-row justify-start items-start gap-1 w-full mt-7 h-fit">
          <SearchParamUI
            searchParam={searchParam}
            searchParamChangeHandler={searchParamChangeHandler}
            onSubmit={handleSearch}
          />
          <div className="border border-gray-300 rounded flex min-w-0 flex-1 flex-col p-4 justify-center items-center gap-3 overflow-x-hidden">
            <SelectableButtons
              label="ریسک پروفایل"
              name="riskProfileId"
              onChange={riskProfileChangeHandler}
              options={riskProfiles}
              value={serverSearchParams?.riskProfileId}
              hideSelectAll
            />
            <CoveredCallOffsetContext.Provider
              value={{ riskProfileId: serverSearchParams?.riskProfileId }}
            >
              <TableTemplate<
                IEnterPairPositionSearchParams,
                ICoveredCallOffsetTableReport
              >
                TableUI={({
                  reports,
                  onAction,
                  sortingType,
                  setSortingType,
                }) => (
                  <CommonTableUI
                    AdditionalBody={Additional}
                    reports={reports}
                    onAction={onAction}
                    filterCols={getFilterCols()}
                    sortingType={sortingType}
                    setSortingType={setSortingType}
                    refreshFlag={refreshFlag}
                    onTableRowClick={(row) => {
                      if (prevRowRef.current === row.id) return;
                      prevRowRef.current = row.id;
                      setSelectedTableInfo({
                        id: row.id,
                        insIds: [
                          row.instrumentId1,
                          row.instrumentId2,
                          row.instrumentId3,
                          row.instrumentId4,
                        ],
                      });
                    }}
                    activeRowId={
                      selectedTableInfo ? selectedTableInfo?.id : undefined
                    }
                    tableBoxClassName="max-h-[500px]"
                    ref={prevRowRef}
                    getExtraTypeUI={getExtraTypeUI}
                  />
                )}
                refreshTableTime={30000}
                getDataTable={getOffsetTable}
                searchParameters={serverSearchParams}
                getExtraTypeUI={getExtraTypeUI}
                withSort
              />
            </CoveredCallOffsetContext.Provider>
          </div>
        </div>
      </div>
    </>
  );
};

export default Offset;
